import { useEffect, useMemo } from 'react';

import { UseBaseTable } from 'hooks/useBaseTable';
import { DEFAULT_ERROR, useCustomSnackBar } from 'hooks/useCustomSnackBar';
import useEventWithDimensions from 'hooks/useEventWithDimensions';

import {
    arrayBufferToString,
    generateTabs,
} from 'components/Dashboards/Project/Components/VersionCompare/utils/helper';
import { UseLargestFloatChangesProvider } from 'components/widgets/LargestFloatChanges/LargestFloatChanges.provider';

interface Props {
    ui: UseLargestFloatChangesProvider['ui'];
    api: UseLargestFloatChangesProvider['api'];
    title: string;
    subTitle: string;
    table: UseBaseTable['table'];
    isSinglePage: boolean;
}

export const useLargestFloatChangesLogic = ({ ui, api, title, subTitle, table, isSinglePage }: Props) => {
    const { handleSnackBar } = useCustomSnackBar();
    const { sendEventWithDimensions } = useEventWithDimensions();

    const activeData = useMemo(() => {
        if (
            api.getLargestFloatChangesTableData.instance.data &&
            api.getLargestFloatChangesTableData.instance.data.periodItemsList
        ) {
            const periodItemsList = api.getLargestFloatChangesTableData.instance.data.periodItemsList;
            return (
                periodItemsList.find((item) => item.id === ui.state.activeTab)?.itemsList ||
                periodItemsList[0].itemsList
            );
        }
        return [];
    }, [api.getLargestFloatChangesTableData.instance.data, ui.state.activeTab]);

    const tableData = useMemo(() => {
        const statusArray = ui.state.statusFilter?.map((item) => item.value);

        if (activeData) {
            return activeData
                .filter((item) => statusArray?.includes(item.latestVersion.cardBoardViewResponse.status.name))
                .filter((item) =>
                    ui.state.isCriticalPath
                        ? item.latestVersion.cardBoardViewResponse.cardIndicators.isCriticalPath.isIndicatorOn ===
                          ui.state.isCriticalPath
                        : item,
                );
        }
        return null;
    }, [activeData, ui.state.isCriticalPath, ui.state.statusFilter]);

    const handleAPIExcelExport = () => {
        api.exportCsvReport.instance.mutate(api.exportCsvReport.payload, {
            onSuccess: () => {
                handleSnackBar('File Successfully Downloaded', 'success');

                sendEventWithDimensions({
                    category: 'Export',
                    action: 'Excel',
                    label: `${title} ${subTitle}`,
                });
            },
            onError: (error) => {
                const message = arrayBufferToString(error?.response?.data);
                DEFAULT_ERROR({ error, message, handleError: handleSnackBar });
            },
        });
    };

    const tabsArrData = generateTabs({ data: api.getLargestFloatChangesTableData.instance.data });

    useEffect(() => {
        if (isSinglePage) {
            table.setPagination({ page: 1, size: tableData?.length });
        }
    }, [isSinglePage, tableData]);
    return {
        logic: {
            tableData,
            tabsArrData,
            handleAPIExcelExport,
        },
    };
};
